import { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import ToastMessages from '../../components/ToastMessages/toastMessages';
import { api } from '../../services/api';
import { calculateFreightSuccess } from '../../store/ducks/freight/actions';

export enum freightTypeEnum {
  PRIVATE = 'Privado',
  PUBLIC = 'Publico',
  INTERNAL = 'Interno',
}

interface IConfigureFreightData {
  driverId?: string;
  vehicleId?: string;
  freightRequestId: string;
  totalWeight: number;
  totalDeliveries: number;
  totalValue: number;
  numberOfHelpers: number;
  vehicleTypeId?: string;
  extraCartIds?: string[];
  pickUpDate?: string;
  pickUpTime?: string;
  deliveryLimitDate?: string;
  freightType?: string;
  percentageFreightCost?: string;
  waypoints?: (
    | {
        corporateName: any;
        before: string;
      }
    | {
        corporateName: any;
        before?: undefined;
      }
  )[];
}

export interface CalculateFreightResponse {
  totalKilometers: number;
  vehicleType: string;
  waypoints: string[];
  freightCost: number;
  totalValue: number;
  priceWithFee: number;
  totalWeight?: number;
  totalDeliveries?: number;
  deliveries?: any[];
}

export function useConfigureFreight() {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const requestIdRef = useRef<number>(0);

  async function execute(
    body: IConfigureFreightData,
    callback?: () => void,
  ): Promise<CalculateFreightResponse | undefined> {
    const currentRequestId = ++requestIdRef.current;

    try {
      setLoading(true);
      
      const payload = {
        driverId: body.driverId,
        vehicleId: body.vehicleId,
        freightRequestId: body.freightRequestId,
        numberOfHelpers: body.numberOfHelpers,
        totalWeight: body.totalWeight,
        totalDeliveries: body.totalDeliveries,
        totalValue: body.totalValue,
        vehicleTypeId: body.vehicleTypeId,
        extraCartIds: body.extraCartIds,
        pickUpDate: body.pickUpDate,
        pickUpTime: body.pickUpTime,
        deliveryLimitDate: body.deliveryLimitDate,
        freightType: body.freightType,
        waypoints: body.waypoints,
        percentageFreightCost: body.percentageFreightCost,
      };
      
      const response = await api.post('v1/backoffice/configure-freight', payload);

      if (currentRequestId !== requestIdRef.current) {
        return undefined;
      }

      if (response.status === 201) {
        const responseData: CalculateFreightResponse = {
          totalKilometers: response.data.totalKilometers,
          vehicleType: response.data.vehicleType || '',
          waypoints: response.data.waypoints || [],
          freightCost: response.data.freightCost,
          totalValue: response.data.totalValue,
          priceWithFee: response.data.priceWithFee,
          totalWeight: response.data.totalWeight,
          totalDeliveries: response.data.totalDeliveries,
          deliveries: response.data.deliveries || [],
        };
        
        if (currentRequestId !== requestIdRef.current) {
          return undefined;
        }
        
        dispatch(
          calculateFreightSuccess({
            totalValue: responseData.totalValue,
            deliveries: responseData.deliveries || [],
            freightCost: responseData.freightCost,
            totalKilometers: responseData.totalKilometers,
            priceWithFee: responseData.priceWithFee,
            vehicleType: responseData.vehicleType,
            numberOfHelpers: body.numberOfHelpers,
            totalWeight: responseData.totalWeight || body.totalWeight,
            totalDeliveries: responseData.totalDeliveries,
          }),
        );

        if (callback) {
          callback();
        }
        return responseData;
      } else {
        return undefined;
      }
    } catch (error: any) {
      toast.error(ToastMessages.general.error.genericError);
      return undefined;
    } finally {
      setLoading(false);
    }
  }
  return {
    execute,
    loading,
  };
}
